import React, { useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const SEND_ENDPOINT = "/api/send-equipment-request";
const PURPOSES = ["Movie", "Commercials", "Rental", "Others"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+\-\s()]{7,20}$/;

export const EquipmentRequestDialog = ({ trigger }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", purpose: "" });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (!form.email.trim() || !EMAIL_RE.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!form.phone.trim() || !PHONE_RE.test(form.phone.trim())) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    if (!form.purpose) {
      toast.error("Please select a purpose.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(SEND_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          purpose: form.purpose,
        }),
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        // Non-JSON response (e.g. an HTML error page) — fall through to the generic message below.
      }
      if (!res.ok || !(data && data.success)) {
        throw new Error((data && data.message) || "Failed to send request. Please try again.");
      }
      toast.success("Request sent — our team will share the inventory with you shortly.");
      setForm({ name: "", email: "", phone: "", purpose: "" });
      setOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to send request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="bg-[#0d0d0d] border-border text-foreground w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto p-5 sm:p-6 rounded-none"
        data-testid="equipment-request-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-xl sm:text-2xl tracking-tight">
            Request Equipment Inventory
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs sm:text-sm">
            Share your details and we&apos;ll send the full CF Action Design equipment inventory.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} noValidate className="space-y-5 mt-2">
          <div className="space-y-2">
            <Label htmlFor="eq-name" className="kicker">Name</Label>
            <Input id="eq-name" data-testid="eq-name" value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Your name"
              className="bg-background border-border focus-visible:ring-[hsl(var(--accent))]" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="eq-email" className="kicker">Email</Label>
            <Input id="eq-email" data-testid="eq-email" type="email" value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="you@example.com"
              className="bg-background border-border focus-visible:ring-[hsl(var(--accent))]" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="eq-phone" className="kicker">Phone</Label>
            <Input id="eq-phone" data-testid="eq-phone" type="tel" value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="+91 ..."
              className="bg-background border-border focus-visible:ring-[hsl(var(--accent))]" />
          </div>
          <div className="space-y-2">
            <Label className="kicker">Purpose</Label>
            <Select value={form.purpose} onValueChange={(v) => update("purpose", v)}>
              <SelectTrigger data-testid="eq-purpose" className="bg-background border-border">
                <SelectValue placeholder="Select purpose" />
              </SelectTrigger>
              <SelectContent className="bg-[#0d0d0d] border-border text-foreground">
                {PURPOSES.map((p) => (
                  <SelectItem key={p} value={p} data-testid={`eq-purpose-${p.toLowerCase()}`}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[hsl(var(--accent))] text-white py-3 font-mono-c text-xs tracking-[0.18em] uppercase hover:bg-white hover:text-background transition-colors disabled:opacity-60"
            data-testid="eq-submit"
          >
            {loading ? "Sending…" : "Send Request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
